import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { Section, Pill, goToStripe } from "@/components/connect/shared";

const ACTIVE = ["active", "trialing"];

// After Checkout, Stripe returns ?subscription_session=cs_... -> sync it right away (webhooks also update it).
function useSyncReturn() {
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const sid = params.get("subscription_session");
  useEffect(() => {
    if (!sid) return;
    api.post("/connect/subscription/sync", { session_id: sid })
      .then(() => toast.success("Welcome to FairStay Host Pro"))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => {
        qc.invalidateQueries({ queryKey: ["connect-subscription"] });
        setParams({}, { replace: true });
      });
  }, [sid, qc, setParams]);
}

function PlanStatus({ pro }) {
  const active = ACTIVE.includes(pro?.status);
  const ends = pro?.current_period_end ? new Date(pro.current_period_end * 1000).toLocaleDateString() : "";
  if (!pro) return <p className="text-sm text-stone-600" data-testid="host-pro-status">You're not subscribed yet.</p>;
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="host-pro-status">
      <Pill ok={active} testid="host-pro-status-pill">{pro.status}</Pill>
      {pro.cancel_at_period_end && <span className="text-amber-700">Cancels on {ends}</span>}
      {pro.paused && <span className="text-amber-700">Payments paused</span>}
      {active && !pro.cancel_at_period_end && ends && <span className="text-stone-500">Renews {ends}</span>}
    </div>
  );
}

export function HostProCard() {
  useSyncReturn();
  const { data } = useQuery({ queryKey: ["connect-subscription"], queryFn: () => api.get("/connect/subscription").then((r) => r.data) });
  const pro = data?.host_pro;
  const active = ACTIVE.includes(pro?.status);
  return (
    <Section testid="host-pro-card" icon={Sparkles} title="FairStay Host Pro · $9.99/month" subtitle="A sample platform subscription, billed to your connected account.">
      <PlanStatus pro={pro} />
      <div className="mt-4 flex flex-wrap gap-3">
        {!active && <Button className="rounded-full" data-testid="host-pro-subscribe-button" onClick={() => goToStripe("/connect/subscribe")}>Subscribe</Button>}
        {pro && <Button variant="outline" className="rounded-full" data-testid="host-pro-manage-button" onClick={() => goToStripe("/connect/billing-portal")}>Manage billing</Button>}
      </div>
    </Section>
  );
}
